import { compact, find, head, values } from "lodash";
import { stringifyUrl } from "query-string";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  matchPath,
  Redirect,
  RouteComponentProps,
  Switch
} from "react-router-dom";
import ContactActionsButton from "../../../components/ContactActionsButton";
import DetailsPagination from "../../../components/DetailsPagination";
import Labels from "../../../components/labels/Labels";
import NavLink from "../../../components/NavLink";
import { useComments } from "../../../context/comments";
import { useContacts } from "../../../context/contacts";
import { useFilteredList } from "../../../context/filteredList";
import { useHotspots } from "../../../context/hotspots";
import { useInstalls } from "../../../context/installs";
import { getContactInstallCommissions } from "../../../context/installs/utils";
import { useLocations } from "../../../context/locations";
import { useMembers } from "../../../context/members";
import { getMemberAvatarUrl } from "../../../context/members/utils";
import PrivateRoute from "../../../layout/PrivateRoute";
import { avatarsApi, Entity } from "../../../utils/constants";
import { Contact } from "../../../utils/types";
import { LabelFilterKey } from "../../../utils/useFilteredLabels";
import useMapCenter, {
  Boundaries,
  Coordinates
} from "../../../utils/useMapCenter";
import { useShouldUpdateMapCentroid } from "../../../utils/useShouldCenterMap";
import ContactFormSidebar from "../../Workspace/ContactsData/ContactFormSidebar";
import { isActiveInstall } from "../../Workspace/InstallsData/utils";
import ContactCommentsTab from "./ContactCommentsTab";
import ContactCommissionsTab from "./ContactCommissionsTab";
import ContactInfoTab from "./ContactInfoTab";
import ContactInstallsTab from "./ContactInstallsTab";
import ContactLocationsTab from "./ContactLocationsTab";
import ContactMessagesTab from "./ContactMessagesTab";
import ContactPaymentsTab from "./ContactPaymentsTab";

interface Props extends RouteComponentProps<{ id: string }> {}

const ContactDetails: React.FC<Props> = (props) => {
  const [center, setCenter, boundaries] = useMapCenter((state) => [
    state.center,
    state.setCenter,
    state.boundaries
  ]);
  const { contacts, currentContact } = useContacts();
  const { members } = useMembers();
  const { hotspotsMap } = useHotspots();
  const { installs } = useInstalls();
  const { locations } = useLocations();
  const { comments } = useComments();
  const [openContactFormSidebar, setOpenContactFormSidebar] = useState(false);
  const [currentlyEditedContact, setCurrentlyEditedContact] = useState<
    Contact | undefined
  >();
  const { filteredList } = useFilteredList();
  const filteredContacts = useMemo(
    () => filteredList[Entity.contact],
    [filteredList]
  );

  const contact = useMemo(
    () =>
      currentContact ||
      (props.match.params.id ? contacts[props.match.params.id] : undefined),
    [contacts, currentContact, props.match.params.id]
  );

  const activeInstallsForCurrentContact = useMemo(
    () =>
      values(installs).filter(
        (install) =>
          !!contact &&
          (contact?.linked_location_ids.includes(install.location_uuid) ||
            !!find(install.commissions, {
              contact_uuid: contact.uuid
            })) &&
          isActiveInstall(install)
      ),
    [contact, installs]
  );

  const mapCentroidConfig: {
    selectedItem: {
      id: string;
      coordinates: Coordinates;
    };
    mapBoundaries?: Boundaries;
    currentMapCentroid: Coordinates;
  } = useMemo(() => {
    let pointDetails = { lng: 0, lat: 0, id: "" };

    if (contact) {
      const installedHotspots = compact(
        activeInstallsForCurrentContact.map(
          ({ hotspot_uuid }) => hotspotsMap[hotspot_uuid]
        )
      );

      const firstActivelyInstalledHotspot = head(installedHotspots);

      const linkedLocations = head(
        compact(contact.linked_location_ids.map((uuid) => locations[uuid]))
      );

      if (firstActivelyInstalledHotspot?.lat) {
        const { id, lat, lng } = firstActivelyInstalledHotspot;
        pointDetails = { lat, lng, id };
      } else if (linkedLocations?.lat) {
        const { uuid, lat, lng } = linkedLocations;
        pointDetails = { lat, lng, id: uuid };
      }
    }

    return {
      selectedItem: {
        coordinates: [pointDetails.lng, pointDetails.lat],
        id: pointDetails.id
      },
      currentMapCentroid: center,
      mapBoundaries: boundaries
    };
  }, [
    activeInstallsForCurrentContact,
    boundaries,
    center,
    contact,
    hotspotsMap,
    locations
  ]);

  const mapCentroid = useShouldUpdateMapCentroid(mapCentroidConfig);

  useEffect(() => {
    setCenter(mapCentroid);
  }, [mapCentroid, setCenter]);

  const commentsCount = useMemo(() => {
    if (!contact) {
      return 0;
    }

    if (comments[contact.uuid]) {
      return comments[contact.uuid].length;
    }

    return 0;
  }, [comments, contact]);

  const locationsCount = useMemo(() => {
    if (!contact) {
      return 0;
    }

    return contact.linked_location_ids.length;
  }, [contact]);

  const paymentsCount = useMemo(() => {
    if (!contact) {
      return 0;
    }

    return contact.contact_payments.length;
  }, [contact]);

  const messagesCount = useMemo(() => {
    if (!contact) {
      return 0;
    }

    return contact.total_messages;
  }, [contact]);

  const commissionsCount = useMemo(() => {
    const activeInstallContactCommissions = getContactInstallCommissions(
      props.match.params.id,
      activeInstallsForCurrentContact
    );

    return activeInstallContactCommissions.length;
  }, [activeInstallsForCurrentContact, props.match.params.id]);

  const member = useMemo(
    () => find(members, { email: contact?.email || "" }),
    [members, contact]
  );

  const tabs = useMemo(
    () => [
      { key: "info", title: "Info", count: 0 },
      {
        key: "locations",
        title: "Locations",
        count: locationsCount
      },
      {
        key: "installs",
        title: "Installs",
        count: activeInstallsForCurrentContact.length
      },
      {
        key: "commissions",
        title: "Commissions",
        count: commissionsCount
      },
      {
        key: "payments",
        title: "Payments",
        count: paymentsCount
      },
      {
        key: "comments",
        title: "Comments",
        count: commentsCount
      },
      {
        key: "messages",
        title: "Host Message",
        count: messagesCount
      }
    ],
    [
      activeInstallsForCurrentContact.length,
      commentsCount,
      commissionsCount,
      locationsCount,
      paymentsCount,
      messagesCount
    ]
  );

  const toggleEditContactForm = useCallback(
    (contact: Contact | undefined) => {
      if (openContactFormSidebar) {
        setCurrentlyEditedContact(undefined);
        setOpenContactFormSidebar(false);
      } else {
        setCurrentlyEditedContact(contact);
        setOpenContactFormSidebar(true);
      }
    },
    [openContactFormSidebar]
  );

  const detailsPageMatch = matchPath<{
    id: string;
    section: string;
  }>(props.location.pathname, {
    path: `/contacts/:id/:section`
  });

  const renderLabels = useMemo(() => {
    if (!contact)
      return (
        <span className="flex w-10 h-6 animate-pulse bg-gray-400 rounded sm:w-40 mt-4 mb-2" />
      );

    return (
      <Labels
        className="mt-2"
        entity={Entity.contact}
        linkedUuid={contact.uuid}
        filterKey={LabelFilterKey.mapContacts}
      />
    );
  }, [contact]);

  return (
    <>
      <ContactFormSidebar
        contact={currentlyEditedContact}
        open={openContactFormSidebar}
        toggleEditContactForm={toggleEditContactForm}
      />

      <div className="pb-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="-mt-12 sm:-mt-16">
            <div className="flex justify-between">
              <img
                className="h-24 w-24 sm:h-32 sm:w-32 z-10 rounded-full"
                src={
                  contact?.email && member
                    ? getMemberAvatarUrl(member, 40)
                    : stringifyUrl({
                        url: `${avatarsApi}/human/${props.match.params.id}.svg`,
                        query: { w: 40, h: 40 }
                      })
                }
                onError={(e: any) => {
                  e.target.onerror = null;
                  e.target.src = stringifyUrl({
                    url: `${avatarsApi}/human/${props.match.params.id}.svg`,
                    query: {
                      w: 40,
                      h: 40
                    }
                  });
                }}
                alt="Contact avatar"
              />
              {contact && (
                <div className="flex items-center">
                  <DetailsPagination
                    path="contacts"
                    selectedItemUuid={contact.uuid}
                    data={filteredContacts}
                    idKey="uuid"
                  />
                </div>
              )}
            </div>
            <div className="sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
              <div className="mt-6 min-w-0 flex-1">
                <div className="flex justify-between">
                  {contact ? (
                    <h1 className="text-md sm:text-2xl font-bold text-gray-900 dark:text-white truncate inline-flex items-center">
                      <span>
                        {contact.name} {member && `(${member.name})`}
                      </span>
                    </h1>
                  ) : (
                    <span className="flex w-20 h-6 animate-pulse bg-gray-400 rounded sm:w-56 mt-2" />
                  )}

                  {contact ? (
                    <ContactActionsButton
                      contact={contact}
                      toggleEditContactForm={toggleEditContactForm}
                    />
                  ) : (
                    <div className="flex flex-shrink-0 w-9 h-9 animate-pulse bg-gray-400 rounded-full"></div>
                  )}
                </div>
                {renderLabels}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 sm:mt-2 2xl:mt-5">
          <div className="sm:border-b sm:border-gray-200 dark:border-gray-500 overflow-auto whitespace-nowrap">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <select
                className="sm:hidden block w-full pl-3 py-2 sm:text-sm rounded-md dark:bg-gray-800"
                onChange={(e) => {
                  props.history.push(`${props.match.url}/${e.target.value}`);
                }}
                value={detailsPageMatch?.params.section}
              >
                {tabs.map(({ key, title }) => (
                  <option key={key} value={key}>
                    {title}
                  </option>
                ))}
              </select>
              <div className="hidden sm:block">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  {tabs.map(({ key, title, count }) => (
                    <NavLink
                      key={key}
                      to={`${props.match.url}/${key}`}
                      activeClassName="border-hotspotty-400 text-gray-900 dark:text-white dark:text-white dark:border-white"
                      inactiveClassName="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
                      className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm"
                    >
                      <span>{title}</span>
                      {!!count && (
                        <span className="bg-gray-200 text-gray-500 group-hover:text-gray-700 dark:text-gray-200 dark:bg-gray-600 dark:group-hover:text-gray-200 dark:group-hover:bg-gray-500 hidden ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium md:inline-block">
                          {count}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </div>

        <Switch>
          <PrivateRoute
            path={`${props.match.path}/info`}
            component={ContactInfoTab as any}
          />
          <PrivateRoute
            path={`${props.match.path}/locations`}
            component={ContactLocationsTab as any}
          />
          <PrivateRoute
            path={`${props.match.path}/installs`}
            component={ContactInstallsTab as any}
          />
          <PrivateRoute
            path={`${props.match.path}/commissions`}
            component={ContactCommissionsTab as any}
          />
          <PrivateRoute
            path={`${props.match.path}/payments`}
            component={ContactPaymentsTab as any}
          />
          <PrivateRoute
            path={`${props.match.path}/comments`}
            component={ContactCommentsTab as any}
          />
          <PrivateRoute
            path={`${props.match.path}/messages`}
            component={ContactMessagesTab as any}
          />
          <Redirect to={`${props.match.path}/info`} />
        </Switch>
      </div>
    </>
  );
};

export default ContactDetails;
